import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import { Briefcase, Plus, Users, UserPlus, UserMinus, X, CheckCircle } from 'lucide-react';

const JobManagement = () => {
  const { user, logout } = useAuth();

  const [jobs, setJobs] = useState([]);
  const [vendorUsers, setVendorUsers] = useState([]);
  const [userJobs, setUserJobs] = useState({}); // user_id -> active job
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showJobUsersModal, setShowJobUsersModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobUsers, setJobUsers] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  const [formData, setFormData] = useState({
    job_name: '',
    job_code: '',
    location: '',
    start_date: '',
    end_date: ''
  });

  const [assignData, setAssignData] = useState({
    user_id: '',
    job_id: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [jobsRes, usersRes] = await Promise.all([
        adminAPI.jobs.list(),
        adminAPI.users.list(0, 100, user?.tenant_id, 'vendor_user')
      ]);
      setJobs(jobsRes.data);
      const vendors = usersRes.data.filter(u => u.role === 'vendor_user');
      setVendorUsers(vendors);

      // Fetch active job for each vendor
      const jobMap = {};
      await Promise.all(
        vendors.map(async (v) => {
          try {
            const res = await adminAPI.users.getActiveJob(v.user_id);
            jobMap[v.user_id] = res.data.active_job;
          } catch {
            jobMap[v.user_id] = null;
          }
        })
      );
      setUserJobs(jobMap);
      setError(null);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [user?.tenant_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      await adminAPI.jobs.create(formData);
      showSuccess(`Job "${formData.job_name}" created successfully`);
      setShowCreateModal(false);
      setFormData({ job_name: '', job_code: '', location: '', start_date: '', end_date: '' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create job');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignJob = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      await adminAPI.jobs.assignUser(assignData.job_id, assignData.user_id);
      const job = jobs.find(j => j.job_id === parseInt(assignData.job_id));
      const vendor = vendorUsers.find(v => v.user_id === parseInt(assignData.user_id));
      showSuccess(`"${vendor?.username}" assigned to "${job?.job_name}"`);
      setShowAssignModal(false);
      setAssignData({ user_id: '', job_id: '' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to assign job');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnassign = async (jobId, userId, username) => {
    if (!window.confirm(`Unassign ${username} from this job?`)) return;
    try {
      await adminAPI.jobs.unassignUser(jobId, userId);
      showSuccess(`${username} unassigned successfully`);
      // Refresh job users modal
      const res = await adminAPI.jobs.getUsers(jobId);
      setJobUsers(res.data.assigned_users || []);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to unassign user');
    }
  };

  const openJobUsersModal = async (job) => {
    setSelectedJob(job);
    setShowJobUsersModal(true);
    try {
      const res = await adminAPI.jobs.getUsers(job.job_id);
      setJobUsers(res.data.assigned_users || []);
    } catch {
      setJobUsers([]);
    }
  };

  const styles = `
    .job-page { padding: 32px; max-width: 1200px; margin: 0 auto; }
    .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 32px; }
    .page-title { font-size: 28px; font-weight: 700; color: #1f2937; }
    .page-subtitle { font-size: 14px; color: #6b7280; margin-top: 4px; }
    .btn-primary { display: flex; align-items: center; gap: 8px; padding: 10px 20px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; }
    .btn-primary:hover { opacity: 0.9; }
    .btn-secondary { display: flex; align-items: center; gap: 8px; padding: 8px 16px; background: #f3f4f6; color: #374151; border: none; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; }
    .btn-secondary:hover { background: #e5e7eb; }
    .btn-danger { display: flex; align-items: center; gap: 6px; padding: 6px 12px; background: #fee2e2; color: #dc2626; border: none; border-radius: 6px; font-size: 12px; font-weight: 500; cursor: pointer; }
    .btn-danger:hover { background: #fecaca; }
    .section-title { font-size: 18px; font-weight: 600; color: #1f2937; margin-bottom: 16px; display: flex; align-items: center; gap: 8px; }
    .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 32px; }
    .card { background: white; border-radius: 12px; border: 1px solid #e5e7eb; overflow: hidden; }
    .card-header { padding: 16px 20px; background: #f9fafb; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
    .card-body { padding: 0; }
    .table { width: 100%; border-collapse: collapse; }
    .table th { padding: 12px 16px; text-align: left; font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; background: #f9fafb; border-bottom: 1px solid #e5e7eb; }
    .table td { padding: 12px 16px; font-size: 14px; color: #374151; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }
    .table tr:last-child td { border-bottom: none; }
    .table tr:hover td { background: #f9fafb; }
    .badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 500; }
    .badge-green { background: #d1fae5; color: #065f46; }
    .badge-gray { background: #f3f4f6; color: #6b7280; }
    .badge-blue { background: #dbeafe; color: #1d4ed8; }
    .empty-state { text-align: center; padding: 40px; color: #9ca3af; }
    .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
    .modal { background: white; border-radius: 16px; width: 100%; max-width: 480px; box-shadow: 0 25px 50px rgba(0,0,0,0.2); }
    .modal-header { padding: 20px 24px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
    .modal-title { font-size: 18px; font-weight: 600; color: #1f2937; }
    .modal-body { padding: 24px; }
    .modal-footer { padding: 16px 24px; border-top: 1px solid #e5e7eb; display: flex; justify-content: flex-end; gap: 12px; }
    .form-group { margin-bottom: 16px; }
    .form-label { display: block; font-size: 13px; font-weight: 500; color: #374151; margin-bottom: 6px; }
    .form-input { width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; color: #1f2937; box-sizing: border-box; }
    .form-input:focus { outline: none; border-color: #667eea; box-shadow: 0 0 0 3px rgba(102,126,234,0.1); }
    .form-select { width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; color: #1f2937; background: white; box-sizing: border-box; }
    .form-select:focus { outline: none; border-color: #667eea; }
    .alert-success { padding: 12px 16px; background: #d1fae5; border: 1px solid #6ee7b7; border-radius: 8px; color: #065f46; font-size: 14px; margin-bottom: 20px; display: flex; align-items: center; gap: 8px; }
    .alert-error { padding: 12px 16px; background: #fee2e2; border: 1px solid #fca5a5; border-radius: 8px; color: #dc2626; font-size: 14px; margin-bottom: 20px; }
    .actions-row { display: flex; gap: 8px; }
    .user-job-badge { font-size: 12px; }
  `;

  if (loading) return (
    <DashboardLayout user={user} logout={logout}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <div style={{ textAlign: 'center', color: '#6b7280' }}>Loading...</div>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout user={user} logout={logout}>
      <style>{styles}</style>
      <div className="job-page">

        {/* Header */}
        <div className="page-header">
          <div>
            <div className="page-title">Job Management</div>
            <div className="page-subtitle">Create jobs and assign vendors to them</div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button className="btn-secondary" onClick={() => setShowAssignModal(true)}>
              <UserPlus size={16} /> Assign Job to User
            </button>
            <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={16} /> Create Job
            </button>
          </div>
        </div>

        {/* Alerts */}
        {success && (
          <div className="alert-success">
            <CheckCircle size={16} /> {success}
          </div>
        )}
        {error && (
          <div className="alert-error" onClick={() => setError(null)} style={{ cursor: 'pointer' }}>
            {error} (click to dismiss)
          </div>
        )}

        <div className="grid-2">
          {/* Jobs Table */}
          <div className="card">
            <div className="card-header">
              <div className="section-title" style={{ margin: 0 }}>
                <Briefcase size={18} /> Jobs ({jobs.length})
              </div>
            </div>
            <div className="card-body">
              {jobs.length === 0 ? (
                <div className="empty-state">No jobs created yet</div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Job Name</th>
                      <th>Code</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map(job => (
                      <motion.tr key={job.job_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{job.job_name}</div>
                          <div style={{ fontSize: '12px', color: '#9ca3af' }}>{job.location}</div>
                        </td>
                        <td><span className="badge badge-blue">{job.job_code}</span></td>
                        <td>
                          <span className={`badge ${job.is_active ? 'badge-green' : 'badge-gray'}`}>
                            {job.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <button className="btn-secondary" style={{ fontSize: '12px', padding: '5px 10px' }}
                            onClick={() => openJobUsersModal(job)}>
                            <Users size={13} /> View Users
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Vendor Users & Their Jobs */}
          <div className="card">
            <div className="card-header">
              <div className="section-title" style={{ margin: 0 }}>
                <Users size={18} /> Vendors & Assignments ({vendorUsers.length})
              </div>
            </div>
            <div className="card-body">
              {vendorUsers.length === 0 ? (
                <div className="empty-state">No vendor users found</div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Active Job</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendorUsers.map(v => (
                      <motion.tr key={v.user_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{v.username}</div>
                          <div style={{ fontSize: '12px', color: '#9ca3af' }}>{v.email}</div>
                        </td>
                        <td>
                          {userJobs[v.user_id] ? (
                            <span className="badge badge-green user-job-badge">
                              {userJobs[v.user_id].job_name}
                            </span>
                          ) : (
                            <span className="badge badge-gray user-job-badge">Not Assigned</span>
                          )}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Create Job Modal */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title">Create New Job</div>
                <button onClick={() => setShowCreateModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <X size={20} color="#6b7280" />
                </button>
              </div>
              <form onSubmit={handleCreateJob}>
                <div className="modal-body">
                  <div className="form-group">
                    <label className="form-label">Job Name (Location) *</label>
                    <input className="form-input" placeholder="e.g. Pennsylvania" required
                      value={formData.job_name}
                      onChange={e => setFormData({ ...formData, job_name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Job Code *</label>
                    <input className="form-input" placeholder="e.g. PENN001" required
                      value={formData.job_code}
                      onChange={e => setFormData({ ...formData, job_code: e.target.value.toUpperCase() })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Location *</label>
                    <input className="form-input" placeholder="e.g. Pennsylvania, USA" required
                      value={formData.location}
                      onChange={e => setFormData({ ...formData, location: e.target.value })} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div className="form-group">
                      <label className="form-label">Start Date</label>
                      <input className="form-input" type="date"
                        value={formData.start_date}
                        onChange={e => setFormData({ ...formData, start_date: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">End Date</label>
                      <input className="form-input" type="date"
                        value={formData.end_date}
                        onChange={e => setFormData({ ...formData, end_date: e.target.value })} />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={actionLoading}>
                    {actionLoading ? 'Creating...' : 'Create Job'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Assign Job Modal */}
        {showAssignModal && (
          <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title">Assign Job to User</div>
                <button onClick={() => setShowAssignModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <X size={20} color="#6b7280" />
                </button>
              </div>
              <form onSubmit={handleAssignJob}>
                <div className="modal-body">
                  <div className="form-group">
                    <label className="form-label">Select Vendor User *</label>
                    <select className="form-select" required
                      value={assignData.user_id}
                      onChange={e => setAssignData({ ...assignData, user_id: e.target.value })}>
                      <option value="">-- Select User --</option>
                      {vendorUsers.map(v => (
                        <option key={v.user_id} value={v.user_id}>
                          {v.username} {userJobs[v.user_id] ? `(Current: ${userJobs[v.user_id].job_name})` : '(No Job)'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Select Job *</label>
                    <select className="form-select" required
                      value={assignData.job_id}
                      onChange={e => setAssignData({ ...assignData, job_id: e.target.value })}>
                      <option value="">-- Select Job --</option>
                      {jobs.filter(j => j.is_active).map(j => (
                        <option key={j.job_id} value={j.job_id}>
                          {j.job_name} ({j.job_code})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ padding: '10px 12px', background: '#fef3c7', borderRadius: '8px', fontSize: '13px', color: '#92400e' }}>
                    If the user is already assigned to another job, it will be automatically deactivated and history will be preserved.
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={() => setShowAssignModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={actionLoading}>
                    {actionLoading ? 'Assigning...' : 'Assign Job'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Job Users Modal */}
        {showJobUsersModal && selectedJob && (
          <div className="modal-overlay" onClick={() => setShowJobUsersModal(false)}>
            <div className="modal" style={{ maxWidth: '560px' }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title">
                  {selectedJob.job_name} — Assigned Users
                </div>
                <button onClick={() => setShowJobUsersModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <X size={20} color="#6b7280" />
                </button>
              </div>
              <div className="modal-body" style={{ padding: 0 }}>
                {jobUsers.length === 0 ? (
                  <div className="empty-state">No users assigned to this job</div>
                ) : (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Assigned</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobUsers.map(u => (
                        <tr key={u.user_id}>
                          <td style={{ fontWeight: 600 }}>{u.username}</td>
                          <td style={{ fontSize: '12px', color: '#6b7280' }}>{u.email}</td>
                          <td style={{ fontSize: '12px', color: '#9ca3af' }}>
                            {new Date(u.assigned_at).toLocaleDateString()}
                          </td>
                          <td>
                            <button className="btn-danger"
                              onClick={() => handleUnassign(selectedJob.job_id, u.user_id, u.username)}>
                              <UserMinus size={13} /> Unassign
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowJobUsersModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
};

export default JobManagement;
